import { useCallback, useEffect, useRef, useState } from 'react';

const ENERGY_THRESHOLD = 10;
const DEBOUNCE_MS = 200;

export type UseRemoteAudioReturn = {
  setRemoteStream: (stream: MediaStream) => void;
  isAgentSpeaking: boolean;
  stopAudio: () => void;
};

const useRemoteAudio = (): UseRemoteAudioReturn => {
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const speakingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopAnalyser = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (speakingTimeoutRef.current !== null) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
    }
    analyserRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    setIsAgentSpeaking(false);
  }, []);

  const startAnalyser = useCallback((stream: MediaStream) => {
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const detect = () => {
      if (!analyserRef.current) {
        return;
      }
      analyserRef.current.getByteFrequencyData(dataArray);

      const energy =
        dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;

      if (energy > ENERGY_THRESHOLD) {
        setIsAgentSpeaking(true);
        if (speakingTimeoutRef.current !== null) {
          clearTimeout(speakingTimeoutRef.current);
        }
        speakingTimeoutRef.current = setTimeout(() => {
          setIsAgentSpeaking(false);
        }, DEBOUNCE_MS);
      }

      animationFrameRef.current = requestAnimationFrame(detect);
    };

    animationFrameRef.current = requestAnimationFrame(detect);
  }, []);

  const setRemoteStream = useCallback(
    (stream: MediaStream) => {
      stopAnalyser();

      const audio = new Audio();
      audio.srcObject = stream;
      audio.play().catch(() => {});
      audioElementRef.current = audio;

      startAnalyser(stream);
    },
    [stopAnalyser, startAnalyser],
  );

  const stopAudio = useCallback(() => {
    stopAnalyser();

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }
  }, [stopAnalyser]);

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  return {
    setRemoteStream,
    isAgentSpeaking,
    stopAudio,
  };
};

export default useRemoteAudio;
